import { useMemo, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'

import useRegionsListQuery from 'common/api/queries/useRegionsListQuery'
import ConfirmationModal from 'common/components/confirmationModal'
import Drawer from 'common/components/drawer'
import useDisablePointerEventsWhenModalVisible from 'common/components/drawer/useDisablePointerEventsWhenModalVisible'
import { useAppSelector } from 'common/hooks/redux'
import { LanguageRule } from 'features/admin/language/api/api'
import useEditLanguageRuleMutation from 'features/admin/language/api/mutations/useEditLanguageRuleMutation'

import CancelButton from './CancelButton'
import FormContent from './FormContent'
import SubmitButton from './SubmitButton'

type Props = {
  languageRule: LanguageRule | undefined
  isOpen: boolean
  onOpenChange: (value: boolean) => void
}

const EditLanguageRulesDrawer: React.FC<Props> = ({
  languageRule,
  isOpen,
  onOpenChange,
}) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const { data: regions } = useRegionsListQuery()

  const { mutate, isLoading } = useEditLanguageRuleMutation()
  const [isFormDirty, setFormDirty] = useState<boolean>(false)
  const [isConfirmationVisible, setIsConfirmationVisible] =
    useState<boolean>(false)

  const handleSave = (values) => {
    if (languageRule) {
      const payload = {
        rule: values.rule,
        examples: values.examples.map((example) => ({
          exampleInput: example.exampleInput,
          exampleOutput: example.exampleOutput,
        })),
      }

      mutate(
        { ruleId: languageRule?.id, accountId, data: payload },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        }
      )
    }
  }

  const regionValue =
    languageRule?.isoLanguage === 'all'
      ? 'all'
      : regions?.find(
          (region) =>
            region.language_settings?.iso_language === languageRule?.isoLanguage
        )?.id

  useDisablePointerEventsWhenModalVisible(isConfirmationVisible)

  const initialValues = useMemo(
    () =>
      languageRule
        ? {
            region: { value: regionValue },
            rule: languageRule.rule,
            examples: languageRule.examples,
          }
        : undefined,
    [languageRule, regionValue]
  )

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title="Edit language rule"
        dismissible={!isConfirmationVisible}
        onOpenChange={onOpenChange}
        content={
          <div className="w-219 flex flex-col h-full">
            <Form
              initialValues={initialValues}
              onSubmit={handleSave}
              render={({ handleSubmit }) => (
                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col flex-1 h-full"
                >
                  <FormSpy
                    onChange={(props) => {
                      const dirtyFields = Object.keys(props.dirtyFields)

                      setFormDirty(dirtyFields.length > 0)
                    }}
                  />

                  <FormContent
                    title="Edit language rule"
                    description="Edit this language-specific rule to ensure grammatically accurate and fluent output."
                    isEditMode={true}
                  />

                  <div className="flex flex-none justify-end mt-auto w-full py-6 border-t border-gold-400 gap-3">
                    <CancelButton
                      data-testid="language-rule-cancel-button"
                      onClick={() => {
                        if (isFormDirty) {
                          setIsConfirmationVisible(true)
                        } else {
                          onOpenChange(false)
                        }
                      }}
                    />
                    <SubmitButton
                      data-testid="edit-language-rule-submit-button"
                      isLoading={isLoading}
                    >
                      Done
                    </SubmitButton>
                  </div>
                </form>
              )}
            />
          </div>
        }
      />
      <ConfirmationModal
        title="Are you sure you want to leave without saving?"
        confirmationText="Any unsaved instructions and examples will be lost."
        confirmButtonText="Yes, I want to leave"
        cancelButtonText="Go back"
        open={isConfirmationVisible}
        hasConfirmationSlider={false}
        onConfirm={() => {
          onOpenChange(false)
          setIsConfirmationVisible(false)
        }}
        onCancel={() => {
          setIsConfirmationVisible(false)
        }}
      />
    </>
  )
}

export default EditLanguageRulesDrawer
